import { Avatar, Grid, Typography, Link, IconButton, Tooltip } from '@mui/material';
import { ApiTimeTrackingLog, IApiUserSession, timeTrackingService, userService } from '@tap/data-providers';
import { IComponentCss, MdView, useFetchData } from '@tap/mui-ui';
import { ReactElement } from 'react';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useSession } from '@tap/tap-ui';
import { useDocument } from '../../context';
import { useTimeTracking } from './TimeTrackingContext';
import { dateTimeHelper } from '@tap/utils';

/*
*   История изменений
*/

export type TimeTrackingLogProps = {
    documentKey: string,
    ttlList?: ApiTimeTrackingLog[],
    isLoading: boolean,
    user?: IApiUserSession,
    fetchData: () => Promise<ApiTimeTrackingLog[]>,
    openAddTimeTrackingDialog: () => void,
    openEditTimeTrackingDialog: (item: ApiTimeTrackingLog) => void,
    openDeleteTimeTrackingDialog: (item: ApiTimeTrackingLog) => void,
}


const css: IComponentCss = {
    root: {
        mt: -2,
    },
    timeTrackingLog: {
        position: 'relative',
        padding: '8px 0 8px 30px',
        borderBottom: ' 1px solid #e0e0e0',
        ':hover': {
            '[class*=isShowTtlButtons]': {
                display: 'flex',
            },
        },
    },
    ttlHeader: {
        position: 'relative',
        padding: '0px 60px 0px 0px',
    },
    avatar: {
        position: 'absolute',
        width: 18,
        height: 18,
        fontSize: '11px',
        left: 0,
        top: '9px',
    },
    worker: {
        fontWeight: 700,
        fontSize: '14px',
        marginRight: '15px',
    },
    date: {
        fontSize: '14px',
        color: '#999999',
    },
    spentTime: {
        fontWeight: 700,
        fontSize: '14px',
        marginRight: '16px',
    },
    dateChangedText: {
        fontSize: '11px',
        color: '#de350b',
        textTransform: 'uppercase',
        marginTop: '3px',
        marginLeft: '15px',
    },
    ttlButtonsWrap: {
        position: 'absolute',
        right: 0,
        top: '-3px',
        display: 'none',
    },
    ttlButton: {
        color: '#999999',
        marginLeft: '2px',
        '> svg': {
            fontSize: '18px',
        },
    },
}


{/* Шаблон записи и изменениями */ }
const TimeTrackingRow = ({
    timeTrackingLog,
    currentUserId,
    openEditTimeTrackingDialog,
    openDeleteTimeTrackingDialog,
}: {
    timeTrackingLog: ApiTimeTrackingLog,
    currentUserId?: number,
    openEditTimeTrackingDialog: (item: ApiTimeTrackingLog) => void,
    openDeleteTimeTrackingDialog: (item: ApiTimeTrackingLog) => void,
}): ReactElement => (
    <Grid container sx={css.timeTrackingLog}>
        <Avatar sx={css.avatar}>{timeTrackingLog.worker ? userService.getUserFIO(timeTrackingLog.worker)[0] : '?'}</Avatar>
        <Grid container sx={css.ttlHeader}>
            <Grid container>
                <Typography
                    component={Link}
                    sx={css.worker}
                >
                    {timeTrackingLog.worker ? userService.getUserFIO(timeTrackingLog.worker) : timeTrackingLog.workerId}
                </Typography>

                <Typography sx={css.spentTime}>
                    {dateTimeHelper.timeFormat(timeTrackingLog.spentMin * 60 * 1000, { withSec: false, hideDays: false, hideZeroHours: false })}
                </Typography>

                <Typography sx={css.date}>
                    {dateTimeHelper.format(timeTrackingLog.startAtUtc, 'dd MMM yyyy в HH:mm', true)}
                </Typography>

                {(timeTrackingLog?.changedAtUtc && timeTrackingLog.changedAtUtc !== timeTrackingLog.createdAtUtc) &&
                    <>
                        <Tooltip title={dateTimeHelper.format(timeTrackingLog.changedAtUtc, 'dd MMM yyyy в HH:mm', true)}>
                            <Typography sx={css.dateChangedText}>
                                Изменён
                            </Typography>
                        </Tooltip>

                    </>
                }
            </Grid>

            {currentUserId === timeTrackingLog.authorId &&
                <Grid sx={css.ttlButtonsWrap} className='isShowTtlButtons'>
                    <IconButton
                        size='small'
                        sx={css.ttlButton}
                        onClick={() => openEditTimeTrackingDialog(timeTrackingLog)}
                    >
                        <EditOutlinedIcon />
                    </IconButton>
                    <IconButton
                        size='small'
                        sx={css.ttlButton}
                        onClick={() => openDeleteTimeTrackingDialog(timeTrackingLog)}
                    >
                        <ClearOutlinedIcon />
                    </IconButton>
                </Grid>
            }
        </Grid>
        <MdView mdContent={timeTrackingLog.description}></MdView>
    </Grid >
);

export const useTimeTrackingLog = ({
    openAddTimeTrackingDialog,
    openEditTimeTrackingDialog,
    openDeleteTimeTrackingDialog,
}: {
    openAddTimeTrackingDialog: () => void,
    openEditTimeTrackingDialog: (item: ApiTimeTrackingLog) => void,
    openDeleteTimeTrackingDialog: (item: ApiTimeTrackingLog) => void,
}): TimeTrackingLogProps => {
    const { documentKey, componentProps } = useDocument();
    const { user } = useSession();
    const withRelatedDocuments = componentProps?.timeTrackingProps?.timeTrackingLogProps?.withRelatedDocuments || false;

    const [{ fetchData }, ttlList, isLoading] = useFetchData(async () => {
        const listResponse = await timeTrackingService.getTimeTrackingList(documentKey, { withRelatedDocuments });
        const authorIds = listResponse.map(c => c.authorId).filter(id => id > 0);
        const workerIds = listResponse.map(c => c.workerId).filter(id => id > 0);
        const userIds = [...authorIds, ...workerIds];
        if (userIds.length === 0)
            return listResponse;
        const users = await userService.getWorkerList({ workerIds: [...new Set(userIds)] });
        listResponse.forEach(c_1 => {
            c_1.author = users.find(u => u.id === c_1.authorId);
            c_1.worker = users.find(u => u.id === c_1.workerId);
        });
        return listResponse;
    }, [documentKey]);

    return {
        documentKey, ttlList, isLoading, user, fetchData,
        openAddTimeTrackingDialog, openEditTimeTrackingDialog, openDeleteTimeTrackingDialog,
    };

}

{/* Список история изменений */ }
export const PureTimeTrackingLog = ({
    isLoading,
    ttlList,
    user,
    openEditTimeTrackingDialog,
    openDeleteTimeTrackingDialog,
}: TimeTrackingLogProps) => {
    return (
        <Grid sx={css.root}>
            {!isLoading && (
                <>
                    {ttlList && ttlList.length > 0
                        ? <>
                            {/* Список работ */}
                            {ttlList.map(ttl => (
                                <TimeTrackingRow
                                    key={ttl.id}
                                    timeTrackingLog={ttl}
                                    currentUserId={user?.userInfo.id}
                                    openEditTimeTrackingDialog={openEditTimeTrackingDialog}
                                    openDeleteTimeTrackingDialog={openDeleteTimeTrackingDialog}
                                />
                            ))}
                        </>
                        : <Typography sx={css.noChange}>Еще не было зарегистрировано никаких работ</Typography>
                    }
                </>
            )}
        </Grid>
    );
}

export const TimeTrackingLog = () => {
    const { timeTrackingLogProps } = useTimeTracking();

    return (
        <PureTimeTrackingLog {...timeTrackingLogProps} />
    );
}
